<template>
    <div ref="wrapRef" class="chart-wrap" :class="{ 'chart-wrap--tall': tall }" @mousemove="onMouseMove" @mouseleave="hoverIdx = null">
        <svg :viewBox="`0 0 ${VIEWBOX_W} ${viewHeight}`" preserveAspectRatio="xMidYMid meet">
            <g class="grid">
                <line
                    v-for="(tick, idx) in yTicks"
                    :key="`y-${idx}`"
                    :x1="X_LEFT"
                    :y1="yFor(tick)"
                    :x2="X_RIGHT"
                    :y2="yFor(tick)"
                    stroke="#EEECE5"
                    stroke-width="0.6"
                    :stroke-dasharray="idx === 0 ? '' : '2,3'"
                />
                <text
                    v-for="(tick, idx) in yTicks"
                    :key="`yt-${idx}`"
                    :x="X_LEFT - 6"
                    :y="yFor(tick) + 3"
                    text-anchor="end"
                    font-size="9"
                    fill="#888888"
                >
                    {{ formatTick(tick) }}
                </text>
            </g>

            <!-- Стек -->
            <g v-if="chartType === 'stacked-bar'">
                <g v-for="bar in stackedBars" :key="`st-${bar.xIdx}-${bar.key}`">
                    <rect
                        :x="bar.x"
                        :y="bar.y"
                        :width="bar.w"
                        :height="bar.h"
                        :fill="bar.color"
                        rx="1"
                        opacity="0.88"
                    />
                </g>
            </g>

            <!-- Круговая диаграмма -->
            <g v-else-if="chartType === 'pie'">
                <g v-for="slice in pieSlices" :key="`pie-${slice.key}`">
                    <path
                        :d="slice.path"
                        :fill="slice.color"
                        stroke="white"
                        stroke-width="1"
                        opacity="0.92"
                    />
                </g>
            </g>

            <!-- Групповые столбцы -->
            <g v-else-if="chartType === 'bar'">
                <rect
                    v-for="bar in groupedBars"
                    :key="`b-${bar.xIdx}-${bar.key}`"
                    :x="bar.x"
                    :y="bar.y"
                    :width="bar.w"
                    :height="bar.h"
                    :fill="bar.color"
                    rx="1"
                    opacity="0.9"
                />
            </g>

            <!-- Линии / область -->
            <g v-else>
                <g v-for="entry in displaySeries" :key="entry.key">
                    <polygon
                        v-if="chartType === 'area'"
                        :points="areaPoints(entry.series)"
                        :fill="colorOf(entry.series, entry.idx)"
                        opacity="0.14"
                    />
                    <polyline
                        v-for="(seg, segIdx) in seriesSegments(entry.series)"
                        :key="`seg-${entry.key}-${segIdx}`"
                        :points="seg.map(p => `${p.x},${p.y}`).join(' ')"
                        fill="none"
                        :stroke="colorOf(entry.series, entry.idx)"
                        :stroke-width="strokeWidthFor(entry.series.style)"
                        :stroke-dasharray="strokeDashFor(entry.series.style)"
                        stroke-linecap="round"
                    />
                    <circle
                        v-for="(pt, pIdx) in seriesCoords(entry.series)"
                        v-show="seriesCoords(entry.series).length <= 24 && pt.value != null"
                        :key="`pt-${entry.key}-${pIdx}`"
                        :cx="pt.x"
                        :cy="pt.y"
                        :r="hoverIdx === pIdx ? 4 : 2.6"
                        :fill="fillForDot(entry.series.style, colorOf(entry.series, entry.idx))"
                        :stroke="colorOf(entry.series, entry.idx)"
                        :stroke-width="hoverIdx === pIdx ? 1.6 : 1.2"
                    />
                </g>
            </g>

            <text
                v-for="(label, idx) in visibleXLabels"
                :key="`x-${idx}`"
                :x="xFor(label.idx)"
                :y="viewHeight - 8"
                text-anchor="middle"
                font-size="9"
                :fill="hoverIdx === label.idx ? '#23282C' : '#5f5f5f'"
                :font-weight="hoverIdx === label.idx ? 600 : 400"
            >
                {{ label.text }}
            </text>

            <line
                v-if="hoverIdx !== null && chartType !== 'bar' && chartType !== 'stacked-bar' && chartType !== 'pie'"
                :x1="xFor(hoverIdx)"
                :y1="Y_TOP"
                :x2="xFor(hoverIdx)"
                :y2="Y_BOTTOM"
                stroke="#23282C"
                stroke-width="0.6"
                stroke-dasharray="3,3"
                opacity="0.45"
            />
        </svg>

        <div v-if="hoverIdx !== null && tooltipStyle" class="tip" :style="tooltipStyle">
            <div class="tip-x">{{ xLabelAt(hoverIdx) }}</div>
            <div v-for="row in tooltipRows" :key="row.key" class="tip-row">
                <span class="tip-dot" :style="{ background: row.color }" />
                <span class="tip-name">{{ row.name }}</span>
                <span class="tip-val">{{ row.value }}</span>
            </div>
            <div v-if="tooltipHiddenCount > 0" class="tip-more">
                ещё {{ tooltipHiddenCount }}
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import {
    buildDisplaySeries,
    normalizeChartViewConfig,
    resolveChartPayload,
    seriesTotal,
} from './chartViewConfig'

const props = defineProps({
    payload: { type: Object, required: true },
    viewConfig: { type: Object, default: null },
    maxSeries: { type: Number, default: null },
    maxTooltipRows: { type: Number, default: 8 },
    viewHeight: { type: Number, default: 220 },
    tall: { type: Boolean, default: false },
})

const VIEWBOX_W = 600
const X_LEFT = 48
const X_RIGHT = 580
const Y_TOP = 22
const PALETTE = ['#E30613', '#198FA0', '#F5A623', '#7B61FF', '#0F6E56']

const wrapRef = ref(null)
const hoverIdx = ref(null)

const cfg = computed(() => normalizeChartViewConfig(props.viewConfig))
const chartType = computed(() => cfg.value.chartType)
const chartPayload = computed(() => resolveChartPayload(props.payload, cfg.value))
const Y_BOTTOM = computed(() => props.viewHeight - 50)

const displaySeries = computed(() =>
    buildDisplaySeries(chartPayload.value, {
        hiddenSeries: cfg.value.hiddenSeries,
    }),
)

const yRange = computed(() => {
    const all = []
    const labels = chartPayload.value.xLabels || []
    if (chartType.value === 'stacked-bar') {
        for (let xi = 0; xi < labels.length; xi += 1) {
            let sum = 0
            for (const { series } of displaySeries.value) {
                sum += Number(series.points?.[xi]) || 0
            }
            all.push(sum)
        }
    } else {
        for (const { series } of displaySeries.value) {
            for (const p of series.points || []) {
                if (typeof p === 'number' && !Number.isNaN(p)) all.push(p)
            }
        }
    }
    for (const t of chartPayload.value.yTicks || []) {
        if (typeof t === 'number') all.push(t)
    }
    if (!all.length) return { min: 0, max: 1 }
    const min = Math.min(0, ...all)
    const max = Math.max(...all)
    if (min === max) return { min: min - 1, max: max + 1 }
    const pad = (max - min) * 0.12
    return { min: Math.max(0, min - pad), max: max + pad }
})

const yTicks = computed(() => {
    const explicit = chartPayload.value.yTicks
    if (Array.isArray(explicit) && explicit.length && chartType.value === 'line') return explicit
    const { min, max } = yRange.value
    const target = 5
    const range = max - min
    if (range <= 0) return [min]
    const rawStep = range / (target - 1)
    const mag = Math.pow(10, Math.floor(Math.log10(rawStep)))
    const candidates = [1, 2, 2.5, 5, 10].map(k => k * mag)
    const step = candidates.find(c => c >= rawStep) || candidates[candidates.length - 1]
    const start = Math.floor(min / step) * step
    const ticks = []
    for (let v = start; v <= max + step * 0.5; v += step) {
        ticks.push(Number(v.toFixed(6)))
    }
    return ticks
})

function formatValue(value) {
    if (value == null || !Number.isFinite(value)) return '—'
    const abs = Math.abs(value)
    if (abs >= 1_000_000) return (value / 1_000_000).toLocaleString('ru-RU', { maximumFractionDigits: 2 }) + ' млн'
    if (abs >= 10_000) return (value / 1000).toLocaleString('ru-RU', { maximumFractionDigits: 1 }) + ' тыс'
    return value.toLocaleString('ru-RU')
}

function formatTick(value) {
    if (value == null || !Number.isFinite(value)) return ''
    const abs = Math.abs(value)
    if (abs >= 1_000_000) return (value / 1_000_000).toLocaleString('ru-RU', { maximumFractionDigits: 1 }) + 'M'
    if (abs >= 1_000) return Math.round(value / 1000).toLocaleString('ru-RU') + 'K'
    if (abs >= 10) return value.toFixed(0)
    return value.toFixed(1)
}

function yFor(value) {
    const { min, max } = yRange.value
    const ratio = (value - min) / (max - min)
    return Y_BOTTOM.value - ratio * (Y_BOTTOM.value - Y_TOP)
}

function xFor(index) {
    const n = (chartPayload.value.xLabels || []).length
    if (n <= 1) return X_LEFT
    return X_LEFT + (index * (X_RIGHT - X_LEFT)) / (n - 1)
}

function xLabelAt(idx) {
    return (chartPayload.value.xLabels || [])[idx] || ''
}

function colorOf(series, index) {
    return series?.color || PALETTE[index % PALETTE.length]
}

function legendName(entry) {
    const item = chartPayload.value.legend?.[entry.idx]
    return item?.label || item?.name || entry.series?.id || ''
}

function isNumericPoint(p) {
    return typeof p === 'number' && Number.isFinite(p)
}

function seriesCoords(series) {
    return (series.points || []).map((p, i) => ({
        x: xFor(i),
        y: isNumericPoint(p) ? yFor(p) : null,
        value: isNumericPoint(p) ? p : null,
    }))
}

function seriesSegments(series) {
    const out = []
    let current = []
    for (const c of seriesCoords(series)) {
        if (c.value == null) {
            if (current.length) {
                out.push(current)
                current = []
            }
            continue
        }
        current.push(c)
    }
    if (current.length) out.push(current)
    return out
}

function strokeDashFor(style) {
    if (style === 'dashed') return '4,4'
    if (style === 'dotted') return '1.5,3'
    return ''
}

function strokeWidthFor(style) {
    if (style === 'dashed') return 1.8
    if (style === 'dotted') return 1.6
    return 2.4
}

function fillForDot(style, color) {
    if (style === 'dashed' || style === 'dotted') return 'white'
    return color
}

function areaPoints(series) {
    const segments = seriesSegments(series)
    if (!segments.length) return ''
    const flat = segments.flat()
    if (!flat.length) return ''
    const bottom = Y_BOTTOM.value
    const first = flat[0]
    const last = flat[flat.length - 1]
    const top = flat.map(p => `${p.x},${p.y}`).join(' ')
    return `${first.x},${bottom} ${top} ${last.x},${bottom}`
}

const groupedBars = computed(() => {
    const nX = (chartPayload.value.xLabels || []).length
    const series = displaySeries.value
    if (!nX || !series.length) return []
    const groupW = (X_RIGHT - X_LEFT) / nX
    const barW = Math.max(2, (groupW * 0.72) / series.length)
    const out = []
    for (let xi = 0; xi < nX; xi += 1) {
        const cx = X_LEFT + groupW * xi + groupW / 2
        const offset = (series.length - 1) * barW / 2
        series.forEach((entry, si) => {
            const raw = entry.series.points?.[xi]
            if (!isNumericPoint(raw)) return
            const x = cx - offset + si * barW
            const y = yFor(raw)
            const h = Y_BOTTOM.value - y
            out.push({
                xIdx: xi,
                key: entry.key,
                x,
                y,
                w: barW * 0.92,
                h: Math.max(0, h),
                color: colorOf(entry.series, entry.idx),
            })
        })
    }
    return out
})

const PIE_CX = 300

const pieSlices = computed(() => {
    const entries = displaySeries.value
    const totals = entries
        .map(entry => ({
            key: entry.key,
            name: legendName(entry),
            value: seriesTotal(entry.series),
            color: colorOf(entry.series, entry.idx),
        }))
        .filter(t => t.value > 0)
    const sum = totals.reduce((s, t) => s + t.value, 0)
    if (!sum) return []
    const cy = (props.viewHeight - 50) / 2 + 10
    const r = Math.min(88, (props.viewHeight - 80) / 2 - 8)
    let angle = -Math.PI / 2
    return totals.map(t => {
        const slice = (t.value / sum) * 2 * Math.PI
        const x1 = PIE_CX + r * Math.cos(angle)
        const y1 = cy + r * Math.sin(angle)
        angle += slice
        const x2 = PIE_CX + r * Math.cos(angle)
        const y2 = cy + r * Math.sin(angle)
        const large = slice > Math.PI ? 1 : 0
        const path = `M ${PIE_CX} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`
        return { ...t, path }
    })
})

const stackedBars = computed(() => {
    const nX = (chartPayload.value.xLabels || []).length
    const series = displaySeries.value
    if (!nX || !series.length) return []
    const barW = Math.max(8, ((X_RIGHT - X_LEFT) / nX) * 0.55)
    const out = []
    for (let xi = 0; xi < nX; xi += 1) {
        const cx = xFor(xi)
        let stack = 0
        for (const entry of series) {
            const raw = entry.series.points?.[xi]
            if (!isNumericPoint(raw)) continue
            const yTop = yFor(stack + raw)
            const yBottom = yFor(stack)
            stack += raw
            out.push({
                xIdx: xi,
                key: entry.key,
                x: cx - barW / 2,
                y: yTop,
                w: barW,
                h: Math.max(0, yBottom - yTop),
                color: colorOf(entry.series, entry.idx),
            })
        }
    }
    return out
})

const visibleXLabels = computed(() => {
    if (chartType.value === 'pie') return []
    const labels = chartPayload.value.xLabels || []
    if (!labels.length) return []
    const maxLabels = props.tall ? 16 : 12
    const step = Math.max(1, Math.ceil(labels.length / maxLabels))
    const out = []
    for (let i = 0; i < labels.length; i += step) {
        out.push({ idx: i, text: labels[i] })
    }
    const last = labels.length - 1
    if (out[out.length - 1]?.idx !== last) out.push({ idx: last, text: labels[last] })
    return out
})

const tooltipRows = computed(() => {
    if (hoverIdx.value == null) return []
    const rows = displaySeries.value.map(entry => ({
        key: entry.key,
        name: legendName(entry),
        color: colorOf(entry.series, entry.idx),
        raw: entry.series.points?.[hoverIdx.value],
        value: formatValue(entry.series.points?.[hoverIdx.value]),
    }))
    rows.sort((a, b) => (Number(b.raw) || 0) - (Number(a.raw) || 0))
    return rows.slice(0, props.maxTooltipRows)
})

const tooltipHiddenCount = computed(() => {
    if (hoverIdx.value == null) return 0
    return Math.max(0, displaySeries.value.length - tooltipRows.value.length)
})

function onMouseMove(event) {
    const el = wrapRef.value
    if (!el) return
    const rect = el.getBoundingClientRect()
    if (!rect.width) return
    const xInSvg = ((event.clientX - rect.left) / rect.width) * VIEWBOX_W
    const labels = chartPayload.value.xLabels || []
    if (!labels.length) {
        hoverIdx.value = null
        return
    }
    let bestIdx = 0
    let bestDist = Infinity
    for (let i = 0; i < labels.length; i += 1) {
        const dist = Math.abs(xFor(i) - xInSvg)
        if (dist < bestDist) {
            bestDist = dist
            bestIdx = i
        }
    }
    hoverIdx.value = bestIdx
}

const tooltipStyle = computed(() => {
    if (hoverIdx.value == null) return null
    const el = wrapRef.value
    if (!el) return null
    const rect = el.getBoundingClientRect()
    if (!rect.width) return null
    const xPx = (xFor(hoverIdx.value) / VIEWBOX_W) * rect.width
    const left = Math.min(rect.width - 16, Math.max(8, xPx + 12))
    return { left: `${left}px`, top: '8px' }
})
</script>

<style scoped>
.chart-wrap {
    width: 100%;
    max-width: 100%;
    aspect-ratio: 600 / 220;
    overflow: visible;
    position: relative;
    cursor: crosshair;
}

.chart-wrap--tall {
    aspect-ratio: 600 / 400;
    min-height: 320px;
}

.chart-wrap svg {
    width: 100%;
    height: 100%;
    display: block;
}

.tip {
    position: absolute;
    pointer-events: none;
    background: #23282C;
    color: white;
    padding: 8px 10px;
    border-radius: 6px;
    font-size: 11.5px;
    box-shadow: 0 6px 16px rgba(21, 26, 34, 0.18);
    min-width: 140px;
    max-height: 240px;
    overflow-y: auto;
    z-index: 2;
}

.tip-x {
    font-weight: 700;
    font-size: 11px;
    color: #B7B5AF;
    margin-bottom: 4px;
}

.tip-row {
    display: grid;
    grid-template-columns: 10px 1fr auto;
    align-items: center;
    column-gap: 8px;
    padding: 2px 0;
    font-variant-numeric: tabular-nums;
}

.tip-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
}

.tip-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #e0e0e0;
}

.tip-val {
    font-weight: 700;
}

.tip-more {
    margin-top: 4px;
    padding-top: 4px;
    border-top: 1px solid rgba(255, 255, 255, 0.15);
    font-size: 10px;
    color: #B7B5AF;
}
</style>
